import { HTMLAttributes, useCallback } from "react";
import { onDesktop } from "@teamscheme/theme";
import styled, { css } from "styled-components";
import { useApiClient } from "../../api/ApiClient";
import { useAutoLoadingClientBase } from "../../api/ClientBase";
import { ContractUpgradeProduct } from "../contract-type/ContractTypePage";
import { StaticMemberUpgradeText } from "../contract-type/MemberUpgradeText";
import { ContractType } from "../model/ContractViewModel";
import SimpleCard from "../contract-type/SimpleCard";
import StandardCard from "../contract-type/StandardCard";
import StaticRoutes from "../../static-pages/StaticRoutes";
import { ButtonLink } from "@teamscheme/button-link";
import { TypeCardContainer } from "../contract-type/SelectTypePage";
import { getContractTypeKey } from "../create-contract/ContractTypeName";
import PremiumCard from "../contract-type/PremiumCard";

export interface GetStaticUpgradeProductsResponse {
  products: ContractUpgradeProduct[];
}

function useProducts() {
  const client = useApiClient();
  const { get } = client;
  return useAutoLoadingClientBase(
    useCallback(
      () => get<GetStaticUpgradeProductsResponse>(`contract/upgrade`),
      [get]
    )
  );
}

export function useStaticUpgradeProducts() {
  const products = useProducts();
  function getProduct(type: ContractType) {
    return products.result?.products.find((x) => x.contractType === type);
  }
  function getCreateLink(type: ContractType) {
    return StaticRoutes.createContract.path.replace(
      ":type",
      getContractTypeKey(type)
    );
  }
  const simple = getProduct(ContractType.Simple);
  const standard = getProduct(ContractType.Standard);
  const premium = getProduct(ContractType.Premium);

  return {
    isLoading: products.isLoading,
    error: products.error,
    simple: simple && {
      product: simple,
      link: getCreateLink(simple.contractType),
    },
    standard: standard && {
      product: standard,
      link: getCreateLink(standard.contractType),
    },
    premium: premium && {
      product: premium,
      link: getCreateLink(premium.contractType),
    },
  };
}

export function StaticContractUpgradeCards() {
  const products = useStaticUpgradeProducts();
  return (
    <TypeCardContainer>
      {products.simple ? (
        <SimpleCard
          product={products.simple.product}
          buttonLabel="Velg enkel"
          buttonAction={{
            linkTo: products.simple.link,
          }}
        />
      ) : null}
      {products.standard ? (
        <StandardCard
          product={products.standard.product}
          buttonLabel="Velg Standard"
          buttonAction={{
            linkTo: products.standard.link,
          }}
        />
      ) : null}
      {products.premium ? (
        <PremiumCard
          product={products.premium.product}
          buttonLabel="Velg Premium"
          buttonAction={{
            linkTo: products.premium.link,
          }}
        />
      ) : null}
    </TypeCardContainer>
  );
}

const UpgradeSection = styled(function (
  props: Omit<HTMLAttributes<HTMLDivElement>, "children">
) {
  return (
    <div {...props}>
      <header>
        <h1>Oppgrader og få mer ut av kjøpekontrakten</h1>
        <p>
          <UpgradeIngress />
        </p>
      </header>
      <article>
        <StaticContractUpgradeCards />
        <ButtonLink variant="primary" linkTo={StaticRoutes.aboutUpgrade.path}>
          Mer om oppgradering av kjøpekontrakten
        </ButtonLink>
      </article>
    </div>
  );
})`
  position: relative;

  ${({ theme }) => css`
    margin: ${theme.spacing.space24} auto ${theme.spacing.space64};
    padding: 0 ${theme.spacing.space16};

    header {
      margin-bottom: ${theme.spacing.space48};
    }

    ${onDesktop`
      header {
        text-align: center;
        max-width: ${8 * 96}px;
        margin: 0 auto ${theme.spacing.space48};
      }

      margin: ${theme.spacing.space48} auto ${theme.spacing.space120};
    `}
  `}
`;

export default UpgradeSection;

export function UpgradeIngress() {
  return (
    <>
      NAF Kjøpekontrakt er helt gratis å bruke. Dersom du ønsker å benytte deg
      av funksjoner og tjenester som identifisering og signering med BankID
      eller eierskifte hos Statens vegvesen kan du velge å oppgradere
      kontrakten. <StaticMemberUpgradeText />
    </>
  );
}
